import { NavLink, useNavigate } from "react-router-dom";
import {
    House, ChatCircleDots, Lightbulb, Bell, ChatCircle,
    User, CreditCard, Gear, Question, ShieldCheck, FileText,
    Lifebuoy, SignOut, List, ShieldStar,
} from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { useNotifications } from "../../contexts/NotificationsContext";
import { useMessages } from "../../contexts/MessagesContext";
import { Button } from "../ui/button";
import { toast } from "sonner";
import ThemeToggle from "./ThemeToggle";

const linkBase =
    "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors";
const linkInactive =
    "text-muted-foreground hover:bg-black/5 dark:hover:bg-white/5 hover:text-foreground";
const linkActive =
    "bg-[#6C63FF]/10 text-[#6C63FF] font-bold";

function NavItem({ to, icon: Icon, label, testid, collapsed, end, badge }) {
    return (
        <NavLink
            to={to}
            end={end}
            data-testid={testid}
            className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkInactive}`
            }
            title={collapsed ? label : undefined}
        >
            <span className="relative">
                <Icon size={20} weight="duotone" />
                {badge > 0 && (
                    <span
                        data-testid={`${testid}-badge`}
                        className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF8A00] text-white text-[10px] font-bold flex items-center justify-center"
                    >
                        {badge >= 99 ? "99+" : badge}
                    </span>
                )}
            </span>
            {!collapsed && <span className="truncate">{label}</span>}
        </NavLink>
    );
}

function Section({ title, collapsed, children }) {
    return (
        <div className="mt-4">
            {!collapsed && (
                <div className="px-3 mb-2 text-[10px] font-bold tracking-[0.2em] uppercase text-muted-foreground">
                    {title}
                </div>
            )}
            <div className="space-y-1">{children}</div>
        </div>
    );
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }) {
    const { user, logout } = useAuth();
    const { unreadCount } = useNotifications();
    const { unreadCount: messagesUnread } = useMessages();
    const navigate = useNavigate();

    const handleLogout = async () => {
        try {
            await logout();
            toast.success("Logged out");
        } catch (e) {
            toast.error("Logged out (with errors)");
        } finally {
            navigate("/auth", { replace: true });
        }
    };

    return (
        <>
            {/* Mobile backdrop */}
            {mobileOpen && (
                <div
                    onClick={onMobileClose}
                    className="lg:hidden fixed inset-0 bg-black/40 z-30"
                    data-testid="sidebar-backdrop"
                />
            )}
            <aside
                className={`fixed lg:sticky top-0 left-0 z-40 h-screen
                    border-r border-border bg-card glass-light glass
                    flex flex-col transition-[width,transform] duration-300
                    ${collapsed ? "lg:w-[78px]" : "lg:w-[260px]"}
                    w-[260px]
                    ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
                data-testid="sidebar"
            >
                {/* Brand + collapse */}
                <div className="flex items-center justify-between px-4 py-5 border-b border-border">
                    <div className="flex items-center gap-2 overflow-hidden">
                        <img
                            src="/logo-192.png"
                            alt="Ideas Mine"
                            className="h-9 w-9 rounded-lg shrink-0 object-cover"
                            data-testid="sidebar-logo"
                        />
                        {!collapsed && (
                            <div className="font-display font-black text-lg leading-none tracking-tight">
                                <span className="text-[#6C63FF]">Ideas</span><span className="text-[#FF8A00]">Mine</span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onToggle}
                        className="hidden lg:inline-flex p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
                        data-testid="sidebar-toggle"
                        aria-label="Toggle sidebar"
                    >
                        <List size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-3 py-4">
                    <Section title="Main" collapsed={collapsed}>
                        <NavItem to="/" end icon={House} label="Home" testid="nav-home" collapsed={collapsed} />
                        <NavItem to="/feed" icon={Lightbulb} label="Feed" testid="nav-feed" collapsed={collapsed} />
                        {user && (
                            <>
                                <NavItem to="/ai-chat" icon={ChatCircleDots} label="AI Chat" testid="nav-ai-chat" collapsed={collapsed} />
                                <NavItem to="/notifications" icon={Bell} label="Notifications" testid="nav-notifications" collapsed={collapsed} badge={unreadCount} />
                                <NavItem to="/messages" icon={ChatCircle} label="Messages" testid="nav-messages" collapsed={collapsed} badge={messagesUnread} />
                            </>
                        )}
                    </Section>

                    {user && (
                        <Section title="Account" collapsed={collapsed}>
                            <NavItem to="/profile" icon={User} label="Profile" testid="nav-profile" collapsed={collapsed} />
                            <NavItem to="/subscription" icon={CreditCard} label="Subscription" testid="nav-subscription" collapsed={collapsed} />
                            <NavItem to="/settings" icon={Gear} label="Settings" testid="nav-settings" collapsed={collapsed} />
                        </Section>
                    )}

                    {user?.role === "admin" && (
                        <Section title="Admin" collapsed={collapsed}>
                            <NavItem to="/admin" icon={ShieldStar} label="Admin Dashboard" testid="nav-admin" collapsed={collapsed} />
                        </Section>
                    )}

                    {!user && !collapsed && (
                        <div
                            className="mt-4 rounded-xl border border-[#6C63FF]/30 bg-gradient-to-br from-[#6C63FF]/10 to-[#FF8A00]/10 p-4 space-y-3"
                            data-testid="sidebar-signin-cta"
                        >
                            <div className="text-sm font-semibold leading-tight">
                                Join Ideas Mine free
                            </div>
                            <div className="text-xs text-muted-foreground leading-snug">
                                Post your own ideas, like + save what inspires you, and chat with our AI co-founder.
                            </div>
                            <div className="flex flex-col gap-2">
                                <Button
                                    size="sm"
                                    onClick={() => navigate("/auth?tab=signup")}
                                    className="brand-button w-full"
                                    data-testid="sidebar-signup-btn"
                                >
                                    Sign up free
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => navigate("/auth")}
                                    className="w-full"
                                    data-testid="sidebar-signin-btn"
                                >
                                    Sign in
                                </Button>
                            </div>
                        </div>
                    )}

                    <Section title="Legal & Info" collapsed={collapsed}>
                        <NavItem to="/how-it-works" icon={Question} label="How It Works" testid="nav-how-it-works" collapsed={collapsed} />
                        <NavItem to="/subscription" icon={CreditCard} label="Pricing" testid="nav-pricing" collapsed={collapsed} />
                        <NavItem to="/privacy" icon={ShieldCheck} label="Privacy Policy" testid="nav-privacy" collapsed={collapsed} />
                        <NavItem to="/terms" icon={FileText} label="Terms & Conditions" testid="nav-terms" collapsed={collapsed} />
                        <NavItem to="/contact" icon={Lifebuoy} label="Contact Support" testid="nav-contact" collapsed={collapsed} />
                    </Section>
                </div>

                <div className="border-t border-border p-3">
                    <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} gap-2`}>
                        <ThemeToggle compact={collapsed} />
                        {user && !collapsed && (
                            <Button
                                variant="ghost" size="sm" onClick={handleLogout}
                                data-testid="logout-button"
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <SignOut size={16} className="mr-1.5" /> Logout
                            </Button>
                        )}
                    </div>
                    {!collapsed && (
                        // Tiny build marker — bump this string on every
                        // change so the user can confirm production has
                        // actually picked up the new bundle vs serving a
                        // stale cached one. Visible in sidebar bottom.
                        <div
                            className="mt-2 text-[10px] text-muted-foreground/60 tracking-wider text-center select-none"
                            data-testid="build-version"
                        >
                            v.2026-05-31.dedup-v3
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
